import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import * as deepLinking from 'Store/actions/deepLinking'
import deepLinkService, { parseDeepLink } from 'Utility/deepLinkService'
import { setCookie, expireCookie, getCookie, openUrl } from 'Utility/globalMethods'
import componentConfig from 'Utility/globalConfig'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { FirebaseMessaging } from 'merchant-product-push-plugin'

const normalizePendingNavigation = (rawValue) => {
  if (!rawValue || typeof rawValue !== 'string') {
    return { url: null, deeplink: null, params: {} }
  }

  const trimmedValue = rawValue.trim()

  if (!trimmedValue) {
    return { url: null, deeplink: null, params: {} }
  }

  const { deepLink, otherParams } = parseDeepLink(trimmedValue)
  if (deepLink) {
    return {
      url: trimmedValue,
      deeplink: deepLink,
      params: otherParams
    }
  }

  if (!/^https?:\/\//i.test(trimmedValue) && !trimmedValue.includes('://')) {
    return { url: null, deeplink: trimmedValue, params: {} }
  }

  try {
    const parsedUrl = new URL(trimmedValue)
    const deeplink = parsedUrl.searchParams.get('deeplink')
    const params = {}

    parsedUrl.searchParams.forEach((value, key) => {
      if (key !== 'deeplink') {
        params[key] = value
      }
    })

    return {
      url: trimmedValue,
      deeplink,
      params
    }
  } catch (error) {
    return { url: trimmedValue, deeplink: null, params: {} }
  }
}

const getPendingNavigation = async () => {
  if (typeof FirebaseMessaging.getPendingNavigation === 'function') {
    const result = await FirebaseMessaging.getPendingNavigation()
    const normalizedResult = normalizePendingNavigation(result?.url || result?.deeplink)
    const resultParams = result?.params || {}

    return {
      url: result?.url || normalizedResult.url || null,
      deeplink: normalizedResult.deeplink || result?.deeplink || null,
      params: Object.keys(resultParams).length > 0 ? resultParams : normalizedResult.params
    }
  }

  const { od } = await FirebaseMessaging.getOdFromPreferences()
  return normalizePendingNavigation(od)
}

export const DeepLinkingManager = ({ actions, logged }) => {
  const location = useLocation()
  const origin = window.location.origin || ''
  const loggedRef = useRef(logged)
  const manageDeepLinkRef = useRef(null)
  const supportsMarketingCloud = componentConfig.availableNotifications().includes('marketing_cloud')

  loggedRef.current = logged

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined

    let active = true
    const listenerHandles = []
    let pendingNavigationQueue = Promise.resolve()

    const registerListener = async (source, eventName, listener) => {
      const handle = await source.addListener(eventName, listener)
      if (active) {
        listenerHandles.push(handle)
      } else if (handle && typeof handle.remove === 'function') {
        handle.remove()
      }
    }

    const consumePendingNavigationOnce = async () => {
      const result = await getPendingNavigation()

      if (result.deeplink) {
        await manageDeepLinkRef.current(result.deeplink, result.params)
        await FirebaseMessaging.clearOdFromPreferences()
        return
      }

      if (supportsMarketingCloud && result.url && /^https?:\/\//i.test(result.url)) {
        openUrl(result.url, true)
        await FirebaseMessaging.clearOdFromPreferences()
      }
    }

    const consumePendingNavigation = () => {
      pendingNavigationQueue = pendingNavigationQueue.then(
        consumePendingNavigationOnce,
        consumePendingNavigationOnce
      )
      return pendingNavigationQueue
    }

    registerListener(App, 'appUrlOpen', (event) => {
      const { deepLink, otherParams } = parseDeepLink(event.url)
      if (deepLink) {
        manageDeepLinkRef.current(deepLink, otherParams)
        return
      }

      const { deeplink, params } = normalizePendingNavigation(event.url)
      manageDeepLinkRef.current(deeplink, params)
    })
    registerListener(App, 'resume', consumePendingNavigation)
    if (supportsMarketingCloud) {
      registerListener(FirebaseMessaging, 'notificationActionPerformed', consumePendingNavigation)
    }
    consumePendingNavigation().catch((error) => {
      console.error('[DeepLinkingManager] Error consuming pending navigation', error)
    })

    return () => {
      active = false
      listenerHandles.forEach((handle) => {
        if (handle && typeof handle.remove === 'function') handle.remove()
      })
    }
  }, [])

  useEffect(() => {
    const { deepLink, otherParams } = parseDeepLink(location.search)
    if (deepLink) {
      manageDeepLink(deepLink, otherParams)
      return
    }

    const searchParams = new URLSearchParams(location.search)
    const legacyOtherParams = {}
    const legacyDeepLink = searchParams.get('deeplink')
    searchParams.forEach((value, key) => {
      if (key !== 'deeplink') {
        legacyOtherParams[key] = value
      }
    })
    manageDeepLink(legacyDeepLink, legacyOtherParams)
  }, [location])

  const manageDeepLink = async (deepLink, otherParams) => {
    if (loggedRef.current) {
      // LOGGED user
      if (!deepLink) {
        // Retrieve deepLink from cookie
        deepLink = await getCookie('deeplink')
      }
      if (!otherParams || Object.keys(otherParams).length === 0) {
        // Retrieve other params from cookie
        const otherParamsCookie = await getCookie('DlOtherParams')
        if (otherParamsCookie) {
          try {
            otherParams = JSON.parse(otherParamsCookie)
          } catch (e) {
            otherParams = {}
          }
        }
      }
      if (deepLink && typeof deepLink === 'string' && deepLink.trim() !== '') {
        // Check if deepLink key is defined in deepLinkService and run the action to activate the deep linking
        if (Object.keys(deepLinkService.routes).length > 0 && deepLink.toLowerCase() in deepLinkService.routes) {
          actions.doHandleDeepLinking(deepLink.toUpperCase(), otherParams)
        }
        if (origin) {
          // Remove deeplink cookie
          await expireCookie('deeplink', origin)
          // Remove other params cookie
          if (otherParams && typeof otherParams === 'object' && Object.keys(otherParams).length > 0) {
            await expireCookie('DlOtherParams', origin)
          }
        }
      }
    } else if (deepLink && typeof deepLink === 'string' && deepLink.trim() !== '' && origin) {
      // NOT logged user
      // If user is not logged, save deeplink as cookie before redirecting to login page
      if (deepLink === 'MOS_LOGIN') {
        if (Capacitor.isNativePlatform()) {
          window.sessionStorage.setItem('fromMosDL', true)
          await setCookie('deeplink', deepLink, origin) // 1 day expiration
          // If there are other params, save them as cookie
          if (otherParams && Object.keys(otherParams).length > 0) {
            await setCookie('DlOtherParams', JSON.stringify(otherParams), origin)
          }
          window.location.href = 'login-mos-app'
        } else {
          // TO-DO
          window.location.href = 'login-mos'
        }
      } else {
        await setCookie('deeplink', deepLink, origin) // 1 day expiration
        // If there are other params, save them as cookie
        if (otherParams && Object.keys(otherParams).length > 0) {
          await setCookie('DlOtherParams', JSON.stringify(otherParams), origin)
        }
      }
      // Save other params as cookie
    }
  }

  manageDeepLinkRef.current = manageDeepLink

  return null
}

DeepLinkingManager.propTypes = {
  actions: PropTypes.object.isRequired,
  logged: PropTypes.bool.isRequired
}

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators(deepLinking, dispatch)
})

const mapStateToProps = () => ({

})

export default connect(mapStateToProps, mapDispatchToProps, (stateProps, dispatchProps, mergeProps) => ({
  ...stateProps,
  ...mergeProps,
  actions: dispatchProps
}))(DeepLinkingManager)
